import allValidations from '../validationObjects';
import fromStringWithBreakOrCommaToArray from 'utils/fromStringWithBreakOrCommaToArray';
import isNumeric from 'utils/isNumeric';
import setSaveOfferButton from './setSaveOfferButton';
import { ENUM_POINT_CHANGE_TYPE, OFFER_FIELDS } from './checkContents';
import { StateNames } from '../validationObjects/states';
import { ValidationErrorMessages } from 'constants/index';

const validateOfferInputs = (value: string, keyName: string) => {
  const returnObject = { isValid: true, helperText: '' };
  if (!value) {
    return returnObject;
  }
  if (keyName === 'states' || keyName === 'zips') {
    if (value.includes(',') && value.includes('\n')) {
      returnObject.isValid = false;
      returnObject.helperText = 'You cannot have both commas and line breaks.';
      return returnObject;
    }
    let arrayOfStrings = null;
    if (value && value.includes('\n')) {
      arrayOfStrings = fromStringWithBreakOrCommaToArray(value);
    } else if (value && value.includes(',')) {
      arrayOfStrings = fromStringWithBreakOrCommaToArray(value);
    } else {
      arrayOfStrings = null;
      if (keyName === 'states') {
        const state = value.split(' ').join('');
        if (state.length) {
          if (!allValidations.states[state] && !StateNames[state]) {
            returnObject.isValid = false;
            returnObject.helperText = 'Invalid state.';
          }
        }
      }
      isNumeric;
      if (keyName === 'zips') {
        const zipCode = value.split(' ').join('');
        if (zipCode.length) {
          if (!isNumeric(zipCode) || !(zipCode.length === 5)) {
            returnObject.isValid = false;
            returnObject.helperText = 'Invalid zip code.';
          }
        }
      }
    }
    if (arrayOfStrings) {
      if (keyName === 'states') {
        for (let i = 0; i < arrayOfStrings.length; i++) {
          if (
            !allValidations.states[arrayOfStrings[i]] &&
            !StateNames[arrayOfStrings[i]]
          ) {
            returnObject.isValid = false;
            returnObject.helperText = 'Invalid states.';
            break;
          }
        }
      }
      if (keyName === 'zips') {
        if (arrayOfStrings.length > 50) {
          returnObject.isValid = false;
          returnObject.helperText =
            'Too many zip codes. Please upload the zip codes in a file';
        }
        for (let i = 0; i < arrayOfStrings.length; i++) {
          const zipCode = arrayOfStrings[i];
          if (!isNumeric(zipCode) || !(zipCode.length === 5)) {
            returnObject.isValid = false;
            returnObject.helperText = 'Invalid zip codes.';
            break;
          }
        }
      }
    }
  } else if (
    [
      'mediarate',
      'cap',
      'pointsawarded',
      'goalid',
      'conversioncapvalue',
      'goalcompletionorder',
    ].includes(keyName)
  ) {
    if (value && (isNaN(Number(value)) || Number(value) < 0)) {
      returnObject.isValid = false;
      returnObject.helperText = ValidationErrorMessages.NON_NEGATIVE_NUMBER;
    }
  }
  return returnObject;
};

/**
 * Validates if the provided field is required.
 * @param field - The field to validate.
 * @param offer - The offer object.
 * @returns A boolean indicating whether the field is required.
 */
export const validateOfferRequiredFields = (field: string, offer) => {
  const fieldsToValidate = [
    'label',
    'logourl',
    'headline',
    'description',
    'ctatext',
    'goalid',
    'backgroundimgurl',
    'offername',
    'fullscreenimageurl',
    'category',
    'pointsawarded',
    'ctaurl',
  ];

  const isRequired = fieldsToValidate.includes(field);

  if (field === 'goalcompletioninstructions') {
    const goals = offer.offertunegoals?.filter(
      (goal) => goal.status !== 'delete'
    );

    return goals.length > 1 || isRequired;
  }

  if (field === 'goalcompletionorder') {
    const goals = offer.offertunegoals?.filter(
      (goal) => goal.status !== 'delete'
    );

    return goals.length > 1 || isRequired;
  }

  const ispointschangescheduled = offer.ispointschangescheduled || false;
  if (ispointschangescheduled) {
    if (offer.pointschangetype === ENUM_POINT_CHANGE_TYPE.PROMO) {
      return (
        [
          OFFER_FIELDS.pointsChangeStartDate,
          OFFER_FIELDS.pointsChangeStartValue,
          OFFER_FIELDS.pointsChangeEndDate,
          OFFER_FIELDS.pointsChangeEndValue,
          OFFER_FIELDS.pointsChangeStartDetails,
          OFFER_FIELDS.pointsChangeEndDetails,
        ].includes(field) || isRequired
      );
    } else if (offer.pointschangetype === ENUM_POINT_CHANGE_TYPE.ONETIME) {
      return (
        [
          OFFER_FIELDS.pointsChangeStartDate,
          OFFER_FIELDS.pointsChangeStartValue,
          OFFER_FIELDS.pointsChangeStartDetails,
        ].includes(field) || isRequired
      );
    }
  }

  return isRequired;
};

/**
 * Validates the start and end dates of an offer.
 * @param {Object} offer - The offer object to validate.
 * @returns {boolean} - A boolean indicating whether the start and end dates are valid.
 */
export const validateStartEndDate = (offer) => {
  let isValidDate = false;
  try {
    const { isValidStartDate, isValidEndDate } = setSaveOfferButton(
      offer,
      null,
      false
    );
    const isSelectedValidStartDate = offer?.startdate && isValidStartDate;
    const isSelectedValidEndDate = offer?.enddate ? isValidEndDate : true;
    isValidDate = isSelectedValidStartDate && isSelectedValidEndDate;
    return isValidDate;
  } catch (err) {
    console.error(`validateStartEndDate() `, err);
  } finally {
    return isValidDate;
  }
};

export default validateOfferInputs;
